import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // 1. Supabase Auth로 로그인
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (authError) {
        setError(`로그인 실패: ${authError.message}`);
        setLoading(false);
        return;
      }

      if (!authData.user) {
        setError('로그인에 실패했습니다.');
        setLoading(false);
        return;
      }

      // 2. 프로필 정보 가져오기
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', authData.user.id)
        .single();

      if (profileError) {
        console.error('프로필 조회 오류:', profileError);
        setError('프로필 정보를 불러올 수 없습니다. 다시 시도해주세요.');
        setLoading(false);
        return;
      }

      if (!profile) {
        setError('프로필이 존재하지 않습니다. 회원가입을 다시 진행해주세요.');
        setLoading(false);
        return;
      }

      // 3. 로컬 스토리지에 사용자 정보 저장
      localStorage.setItem('currentUser', JSON.stringify({
        id: authData.user.id,
        email: authData.user.email,
        user_type: profile.user_type,
        username: profile.username,
        full_name: profile.full_name
      }));

      // 4. 사용자별 초기 데이터 설정 (첫 로그인 시)
      const userKey = `user_${authData.user.id}`;
      if (!localStorage.getItem(`${userKey}_initialized`)) {
        // 첫 로그인 시 빈 데이터로 초기화
        localStorage.setItem(`${userKey}_balance`, '0');
        localStorage.setItem(`${userKey}_subscriptions`, '[]');
        localStorage.setItem(`${userKey}_saved`, '[]');
        localStorage.setItem(`${userKey}_initialized`, 'true');
      }

      // 5. 사용자 유형에 따라 대시보드로 이동
      if (profile.user_type === 'fan') {
        navigate('/fan/dashboard', { replace: true });
      } else if (profile.user_type === 'creator') {
        navigate('/creator/dashboard', { replace: true });
      } else if (profile.user_type === 'admin') {
        navigate('/admin/dashboard', { replace: true });
      } else {
        // 기본값: 팬 대시보드
        navigate('/fan/dashboard', { replace: true });
      }
    } catch (error) {
      console.error('로그인 오류:', error);
      setError('로그인 중 오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-pink-50 to-purple-50 flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        {/* 로고 */}
        <div className="text-center mb-8">
          <h1 className="text-5xl font-black bg-gradient-to-r from-pink-500 via-purple-500 to-blue-400 bg-clip-text text-transparent mb-2 tracking-tight" style={{ textShadow: '0 0 20px rgba(236, 72, 153, 0.3)' }}>
            MyFanHub
          </h1>
          <div className="flex items-center justify-center gap-2 text-gray-600">
            <p className="text-sm">당신만의 팬 플랫폼</p>
            <div className="flex gap-1">
              <i className="ri-heart-fill text-pink-400 text-lg"></i>
              <i className="ri-star-fill text-yellow-400 text-lg"></i>
            </div>
          </div>
        </div>

        {/* 로그인 폼 */}
        <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl p-8 border border-pink-100">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">로그인</h2>
          
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}
          
          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">이메일</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-pink-400 focus:ring-2 focus:ring-pink-200 outline-none transition-all text-sm"
                placeholder="이메일을 입력하세요"
                required
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">비밀번호</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-pink-400 focus:ring-2 focus:ring-pink-200 outline-none transition-all text-sm"
                placeholder="비밀번호를 입력하세요"
                required
                disabled={loading}
              />
            </div>

            <button
              type="submit"
              className="w-full bg-gradient-to-r from-pink-500 to-purple-500 text-white py-3 rounded-xl font-semibold hover:from-pink-600 hover:to-purple-600 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? '로그인 중...' : '로그인'}
            </button>
          </form>

          <div className="mt-6 text-center text-sm text-gray-600">
            계정이 없으신가요?{' '}
            <Link to="/signup" className="text-pink-500 font-semibold hover:text-pink-600 cursor-pointer">
              회원가입
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
